import { Component, inject, Input, OnInit } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FormGroup, Validators } from '@angular/forms';
import { AgreementPeriodForm, AgreementReminderForm } from '../../shared-agreement.model';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { CachedValuesService } from '@services/cached-values';
import { Observable, map } from 'rxjs';
import { User } from 'app/types';
import { TranslateService } from '@ngx-translate/core';
import { toDateISOString } from '@helpers/datetime-helper';
import { ListTypeItem } from 'app/types/list-type-item.type';
import { AgreementFormHelper } from '../../shared-agreement.helper';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CalendarModalComponent } from '../calendar-modal/calendar-modal.component';

@UntilDestroy()
@Component({
    selector: 'app-agreement-reminder',
    templateUrl: './reminder.component.html',
    styleUrl: './reminder.component.scss'
})
export class AgreementReminderComponent implements OnInit {
    @Input() public reminderForm: FormGroup<AgreementReminderForm>;
    @Input() public periodForm: FormGroup<AgreementPeriodForm>;
    @Input() public showPotentialValidationMessages: boolean;
    @Input() public agreementName: string;
    @Input() public isCreateMode = false;
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly translateService = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly modalService = inject(NgbModal);

    public reminders: DropdownItem<number>[] = this.cachedValuesService.generateNumericDropdownItems(10, 1);
    public readonly recipients$: Observable<MultiSelectItem[]> = this.cachedValuesService.users$.pipe(
        map((data) =>
            data.map((item: User) => {
                return {
                    id: item.id,
                    name: item.firstName + ' ' + item.lastName // + ' - ' + item.email
                } as MultiSelectItem;
            })
        )
    );

    public reminderPeriods: DropdownItem<number>[] = this.getReminderPeriods();
    public getReminderPeriods(): DropdownItem<number>[] {
        const month = this.translateService.instant('GENERAL.MONTHS') as string;
        const items: DropdownItem<number>[] = [
            { value: 1, label: `${1} ` + month },
            { value: 2, label: `${2} ` + month },
            { value: 3, label: `${3} ` + month },
            { value: 4, label: `${4} ` + month },
            { value: 5, label: `${5} ` + month },
            { value: 6, label: `${6} ` + month },
            { value: 7, label: `${7} ` + month },
            { value: 8, label: `${8} ` + month },
            { value: 9, label: `${9} ` + month },
            { value: 10, label: `${10} ` + month },
            { value: 11, label: `${11} ` + month },
            { value: 12, label: `${12} ` + month },
            { value: 18, label: `${18} ` + month },
            { value: 24, label: `${24} ` + month },
            { value: 36, label: `${36} ` + month }
        ];

        return items;
    }

    public readonly reminderPeriods$: Observable<DropdownItem<number>[]> =
        this.cachedValuesService.commitmentperiodtypes$.pipe(
            map((data) =>
                data.map((item: ListTypeItem) => {
                    return {
                        value: parseInt(item.name),
                        label: this.translateService.instant(
                            'FEATURES.AGREEMENTS.COMMITMENT_PERIOD_TYPES.' + item.name
                        ) as string
                    } as DropdownItem<number>;
                })
            )
        );

    public get f() {
        return this.reminderForm.controls;
    }

    public ngOnInit() {
        console.log('init reminders');
        if (this.reminderForm.disabled) return;
        this.checkInputs();
        this.f.recipients.valueChanges.subscribe(() => {
            this.checkInputs();
        });
    }

    public isDateInPast(date: Date | string): boolean {
        const compareDate = new Date(date);
        const today = new Date();
        // Remove time part for accurate comparison
        compareDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return compareDate < today;
    }

    public get lastDateForNotice(): string {
        if (this.noticeDate) {
            return 'Baserat på uppsägningtid ' + toDateISOString(this.noticeDate);
        }
        return '';
    }

    public get reminderStartDate() {
        let startDate: Date;
        if (this.noticeDate) {
            startDate = new Date(this.noticeDate);
            startDate.setMonth(this.noticeDate.getMonth() - (this.f.reminderPeriod.value ?? 0));
        }
        return startDate;
    }

    public get noticeDate() {
        return AgreementFormHelper.getNoticeDate(this.periodForm?.controls);
    }

    public getReminderDates(): Date[] {
        if (!this.noticeDate) return [];
        return AgreementFormHelper.computeReminderDates(
            this.noticeDate,
            this.f.reminderPeriod.value ?? 0,
            this.f.numOfReminders.value ?? 1
        );
    }

    public openCalendarModal(): void {
        const dates = this.getReminderDates();
        const isEditMode = !this.router.url.includes('create-new-agreement');
        const ref = this.modalService.open(CalendarModalComponent, { size: 'lg' });
        const instance = ref.componentInstance as CalendarModalComponent;
        instance.dates = dates;
        instance.agreementName = this.agreementName;
        instance.agreementUrl = isEditMode ? window.location.href : null;
    }

    private checkInputs() {
        if (this.f.recipients.value?.length > 0) {
            this.f.numOfReminders.enable();
            this.f.escalatedRecipients.enable();
            this.f.reminderPeriod.enable();
            this.f.reminderPeriod.setValidators(Validators.required);
            this.f.reminderPeriod.updateValueAndValidity();
        } else {
            this.f.numOfReminders.disable();
            this.f.escalatedRecipients.disable();
            this.f.reminderPeriod.disable();
            this.f.reminderPeriod.clearValidators();
            this.f.reminderPeriod.updateValueAndValidity();
        }
    }
}
